import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { PRICING_TIERS } from '@/utils/constants';
import { formatCurrency, formatCompactNumber } from '@/utils/formatters';

export function SelectUserTypePage() {
  const navigate = useNavigate();

  const handleSelectUserType = (userType: 'casual' | 'pro') => {
    // Store user type in localStorage (will be replaced with real auth later)
    localStorage.setItem('vortex_user_type', userType);
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-900 via-primary-800 to-primary-700">
      {/* Header */}
      <header className="border-b border-white/10 bg-primary-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              {/* Logo */}
              <img
                src="/assets/vortex-logo-white.svg"
                alt="Vortex Marine"
                className="h-12"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              ← Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <Badge variant="info" size="lg" className="mb-6">
              ⚓ Choose Your Plan
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              How Will You Use Vortex Marine?
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Select the option that best fits your navigation needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Casual User Card */}
            <Card
              variant="elevated"
              padding="xl"
              className="bg-white animate-slide-up"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="text-center mb-6">
                <div className="text-5xl mb-4">🌊</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Casual User</h2>
                <p className="text-gray-600">Perfect for occasional trips</p>
              </div>

              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    Pay As You Go
                  </div>
                  <p className="text-gray-600">No monthly commitment</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <span className="text-gray-700">Base Fee</span>
                    <span className="font-bold text-gray-900">{formatCurrency(PRICING_TIERS.casual.monthlyFee)}</span>
                  </div>
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <span className="text-gray-700">Per 1,000 Tokens</span>
                    <span className="font-bold text-gray-900">${PRICING_TIERS.casual.pricePerThousandTiles}</span>
                  </div>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {PRICING_TIERS.casual.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-green-500 mr-3 text-xl">✓</span>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant="outline"
                size="xl"
                fullWidth
                onClick={() => handleSelectUserType('casual')}
              >
                Start as Casual User
              </Button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Ideal for weekend sailors and occasional voyages
              </p>
            </Card>

            {/* Pro Subscriber Card */}
            <Card
              variant="elevated"
              padding="xl"
              className="bg-gradient-to-br from-primary-600 to-primary-700 ring-4 ring-accent-500 transform lg:scale-105 animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              <div className="text-center mb-6">
                <Badge variant="success" className="mb-4">
                  ⭐ Recommended
                </Badge>
                <div className="text-5xl mb-4">⚓</div>
                <h2 className="text-3xl font-bold text-white mb-2">Pro Subscriber</h2>
                <p className="text-white/80">For serious mariners</p>
              </div>

              <div className="mb-6 pb-6 border-b border-white/20">
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-accent-400 mb-2">
                    {formatCurrency(PRICING_TIERS.pro.monthlyFee)}
                    <span className="text-xl text-white/80">/month</span>
                  </div>
                  <p className="text-white/80">Incredible value for regular use</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                    <span className="text-white/90">Monthly Quota</span>
                    <span className="font-bold text-accent-400">{formatCompactNumber(PRICING_TIERS.pro.tilesPerMonth)} tokens</span>
                  </div>
                  <div className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                    <span className="text-white/90">Overage Rate</span>
                    <span className="font-bold text-accent-400">${PRICING_TIERS.pro.pricePerThousandTiles}/1k tokens</span>
                  </div>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {PRICING_TIERS.pro.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-accent-400 mr-3 text-xl">✓</span>
                    <span className="text-white">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant="accent"
                size="xl"
                fullWidth
                onClick={() => handleSelectUserType('pro')}
              >
                Start Pro Subscription
              </Button>

              <p className="text-center text-sm text-white/70 mt-4">
                Best for cruisers, charter operators, and frequent sailors
              </p>
            </Card>
          </div>

          {/* Help Text */}
          <div className="mt-12 text-center">
            <p className="text-white/60 text-sm">
              Not sure which to choose? Start with Casual and upgrade anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center text-white/60 text-sm">
          <p>© 2026 Vortex Marine. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
